import { useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import { BULK_DELETE_BATCH_SIZE } from '@/constants/documents'

interface BulkDeleteResult {
  ok: boolean
  failedIds: string[]
  errors?: string[]
}

export function useDeleteDocumentsBulkMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkDeleteResult> => {
      if (ids.length === 0) {
        return { ok: false, failedIds: [] }
      }

      const uniqueIds = [...new Set(ids)]
      const chunks: string[][] = []
      for (let i = 0; i < uniqueIds.length; i += BULK_DELETE_BATCH_SIZE) {
        chunks.push(uniqueIds.slice(i, i + BULK_DELETE_BATCH_SIZE))
      }

      const failedIds: string[] = []
      const errors: string[] = []

      for (const chunk of chunks) {
        try {
          const res = await fetchApi<{
            deletedIds?: string[]
            failed?: { id: string; error?: string }[]
          }>({
            endpoint: '/api/documents/bulk-delete',
            method: 'POST',
            body: { ids: chunk },
          })

          const failed = res.failed ?? []
          failed.forEach((item) => {
            failedIds.push(item.id)
            if (item.error) {
              errors.push(item.error)
            }
          })
        } catch (err) {
          console.error('Error deleting documents in bulk chunk:', err)
          failedIds.push(...chunk)
        }
      }

      return failedIds.length > 0
        ? { ok: false, failedIds, ...(errors.length > 0 ? { errors } : {}) }
        : { ok: true, failedIds: [] }
    },
    onSuccess: (result) => {
      // Invalidate list to refresh even on partial success
      if (result.ok || result.failedIds.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['documents', 'list'] })
      }
    },
  })
}
